import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

export interface StudentProfile {
  studentId: string;
  studentName: string;
  studentImage: string;
  registrationId: string;
  classNumber: string;
  classDivision: string;
  schoolName: string;
  dateOfBirth: string;
  rollNumber: number;
  dateOfAdmission: string;
  gender: string;
  parentName: string;
  mobileNumber: string;
  parentEmail: string | null;
  address: string;
  [key: string]: any;
}

export interface StudentAttendanceInfo {
  TodayAttendance: 'PRESENT' | 'ABSENT' | 'LEAVE';
  MonthDaysPresent: number;
  MonthDays: number;
  AcademicDaysPresent: number;
  AcademicDays: number;
}

export interface StudentSibling {
  id: string;
  studentImage: string;
  studentName: string;
  classNumber: string;
  classDivision: string;
}

export interface StudentClassTeacher {
  id: string;
  staffId: string;
  staffName: string;
  designation: string;
}

export interface StudentSubjectTeacher {
  id: string;
  staffName: string;
  subject: string;
}

export interface StudentFeesDetails {
  totalAmount: number;
  paidAmount: number;
  discountAmount: number;
  balanceAmount: number;
}

export interface GetStudentInfoResponse {
  profile: StudentProfile;
  attendanceInfo: StudentAttendanceInfo;
  siblings: StudentSibling[];
  classTeachers: StudentClassTeacher[];
  staffSubjectInfo: { [subjectName: string]: StudentSubjectTeacher[] };
  feesDetails: StudentFeesDetails | null;
  studentDayTimetable: any[];
}

export interface TermFee {
  TermName: string;
  feesTermAmount: number;
  discount: number;
  Paid: number;
  Balance: number;
  DueDate: string | null;
  lateFee: number;
}

export interface TypeFee {
  Name: string;
  feesTypeAmount: number;
  Discount: number;
  Paid: number;
  Balance: number;
  DueData: string | null; // typo preserved — matches the real API field name
  lateFee: number;
}

export interface OtherFeeTerm {
  Name: string;
  termAmount: number;
  Discount: number;
  Paid: number;
  Balance: number;
  termDueDate: string | null;
  lateFee: number;
}

export interface GetFeesStudentWiseResponse {
  termFees: TermFee[];
  typeFees: TypeFee[];
  otherFeeTerms: OtherFeeTerm[];
}

export type AttendanceDayStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'PARTIAL' | 'LEAVE';

export interface AttendanceCalendarEntry {
  CreatedDate: string;
  AttendanceStatus: AttendanceDayStatus;
}

export interface GetAttendanceCalendarResponse {
  status: boolean;
  body: {
    attendancePercentage: number;
    attendances: AttendanceCalendarEntry[];
  };
}

export interface PartialAttendancePeriod {
  fkPeriodId: number | null;
  subject: string | null;
  Attendance: 'PRESENT' | 'ABSENT' | 'LATE';
  staffImage: string | null;
  staffName: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface GetPartialAttendanceResponse {
  status: boolean;
  body: PartialAttendancePeriod[];
}

export type UploadedDocumentFileType = 'xls' | 'xlsx' | 'pdf' | 'image';

export interface UploadedDocument {
  documentTitle: string;
  documentDescription: string | null;
  documentUrl: string;
  fileType: UploadedDocumentFileType;
  createdOn: string;
}

export interface GetUploadedDocumentsResponse {
  status: boolean;
  body: {
    uploadedDocumentsList: UploadedDocument[];
  };
}

export interface DiaryEntry {
  diaryId: string;
  subjectId: string;
  subject: string;
  HomeworkNumber: string;
  diaryTitle: string | null;
  isAttachmentWithDiary: boolean;
  staffImage: string;
  staffName: string;
  homeworkDonePercentage: number;
  dueDate: string;
}

export interface GetTodaysDiaryResponse {
  status: boolean;
  body: {
    diaryDetailsForTodaysDueDates: DiaryEntry[];
    isStaffAllowedToCreateDiary: boolean;
    academicStartDate: string;
    academicEndDate: string;
    maxPageNumber: number;
  };
}

@Injectable({ providedIn: 'root' })
export class StaffStudentService {
  // /student/info and /students/fee/details/with/staff/permission are not
  // versioned — same host as API_ENDPOINT_STAFF, no /api/v3.
  private baseUrl = AppSettings.API_ENDPOINT_STAFF_BASE;
  // The attendance calendar and document hub endpoints ARE versioned (v3),
  // unlike the two above.
  private v3Url = AppSettings.API_ENDPOINT_STAFF;
  private getStudentInfoEndpoint = apiConstants.getStudentInfo;
  private getFeesStudentWiseEndpoint = apiConstants.getFeesStudentWise;
  private getUploadedDocumentsEndpoint = apiConstants.getUploadedDocuments;
  private getAttendanceCalendarEndpoint = apiConstants.getStudentAttendanceCalendar;
  private getPartialAttendanceEndpoint = apiConstants.getStudentPartialAttendance;
  private getTodaysDiaryDataEndpoint = apiConstants.getTodaysDiaryData;

  constructor(private http: HttpClient) {}

  getStudentInfo(staffId: number | string, studentId: number | string) {
    const body = {
      requestByStaffId: String(staffId),
      studentId: String(studentId),
    };
    return this.http
      .post<GetStudentInfoResponse>(`${this.baseUrl}${this.getStudentInfoEndpoint}`, body)
      .pipe(map((res) => res));
  }

  getFeesStudentWise(classId: number | string, studentId: number | string, staffId: number | string) {
    const body = {
      classId: String(classId),
      studentId: String(studentId),
      staffId: String(staffId),
    };
    return this.http
      .post<GetFeesStudentWiseResponse>(`${this.baseUrl}${this.getFeesStudentWiseEndpoint}`, body)
      .pipe(map((res) => res));
  }

  // date is an "MM/YYYY" month string, e.g. "8/2026".
  getAttendanceCalendar(
    classId: number | string,
    studentId: number | string,
    date: string,
    academicId: number | string,
    schoolId: number | string
  ) {
    const params = new HttpParams()
      .set('classId', classId)
      .set('lang', 'en')
      .set('studentId', studentId)
      .set('attendanceDate', date)
      .set('academicId', academicId)
      .set('schoolId', schoolId);

    return this.http
      .get<GetAttendanceCalendarResponse>(`${this.v3Url}${this.getAttendanceCalendarEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  // date is a "YYYY-MM-DD" day string.
  getPartialAttendance(
    classId: number | string,
    studentId: number | string,
    date: string,
    academicId: number | string
  ) {
    const params = new HttpParams()
      .set('classId', classId)
      .set('lang', 'en')
      .set('studentId', studentId)
      .set('attendanceDate', date)
      .set('academicId', academicId);

    return this.http
      .get<GetPartialAttendanceResponse>(`${this.v3Url}${this.getPartialAttendanceEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  getUploadedDocuments(classId: number | string, studentId: number | string, staffId: number | string) {
    const params = new HttpParams()
      .set('classId', classId)
      .set('studentId', studentId)
      .set('staffId', staffId)
      .set('lang', 'en');

    return this.http
      .get<GetUploadedDocumentsResponse>(`${this.v3Url}${this.getUploadedDocumentsEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  getTodaysDiaryData(classId: number | string, staffId: number | string, pageNumber: number) {
    const body = {
      classId: String(classId),
      staffId: String(staffId),
      pageNumber,
      subjectId: null,
      lang: 'en',
    };
    return this.http
      .post<GetTodaysDiaryResponse>(`${this.v3Url}${this.getTodaysDiaryDataEndpoint}`, body)
      .pipe(map((res) => res));
  }
}
